import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {HcaMemberService} from '../../../../../../shared/_services/hca-member.service';
import {UtilService} from '../../../../../../shared/_services/util.service';

@Component({
  selector: 'app-members-activity-log',
  templateUrl: './members-activity-log.component.html',
  styleUrls: ['./members-activity-log.component.scss']
})
export class MembersActivityLogComponent implements OnInit {
  memberId: number | string;
  activityList: any[] = [];
  totalCount = 0;
  p = 1;
  size = 20;
  loading = false;
  errorMessage = '';
  expandedId: number = null;
  filterForm: FormGroup;

  actionGroups = [
    {value: '', label: 'All groups'},
    {value: 'STAY', label: 'Stays'},
    {value: 'TRANSACTION', label: 'Transactions'},
    {value: 'PROFILE', label: 'Profile'},
    {value: 'MEMBERSHIP', label: 'Membership'},
    {value: 'AUTH', label: 'Access'}
  ];

  private groupLabels: any = {
    STAY: 'Stays',
    TRANSACTION: 'Transactions',
    PROFILE: 'Profile',
    MEMBERSHIP: 'Membership',
    AUTH: 'Access'
  };

  private actionLabels: any = {
    STAY_CREATED: 'Stay created',
    STAY_UPDATED: 'Stay updated',
    STAY_DELETED: 'Stay deleted',
    STAY_STATUS_CHANGED: 'Stay status changed',
    STAY_SYNCED: 'Synced with PMS',
    PMS_SYNC: 'Synced with PMS',
    STAY_CLAIMED: 'Stay claimed',
    TXN_CREATED: 'Transaction created',
    TXN_STATUS_CHANGED: 'Transaction status changed',
    TXN_UPDATED: 'Transaction updated',
    TXN_REJECTED: 'Transaction rejected',
    MEMBER_STATUS_CHANGED: 'Member status changed',
    MEMBER_COMMENT_ADDED: 'Admin comment',
    MEMBER_ACCOUNTS_MERGED: 'Accounts merged',
    MEMBER_MERGE_REJECTED: 'Merge rejected',
    MEMBER_UPGRADED: 'Upgrade',
    MEMBER_DOWNGRADED: 'Downgrade',
    MEMBER_HARD_DELETED: 'Permanent deletion',
    LOGIN_AS: 'Login as'
  };

  private triggerLabels: any = {
    MANUAL: 'Manual (admin)',
    AUTO: 'Automatic',
    BOOKING_CHECKOUT: 'By checkout'
  };

  private sensitiveKeys = ['token', 'password', 'memberToken'];

  constructor(
    private hcaMemberService: HcaMemberService,
    public utilService: UtilService
  ) {}

  ngOnInit() {
    this.memberId = this.hcaMemberService.getMemberId;
    this.filterForm = new FormGroup({
      actionGroup: new FormControl(''),
      from: new FormControl(''),
      to: new FormControl('')
    });
    this.loadActivity(0);
  }

  loadActivity(page: number) {
    if (!this.memberId) {
      this.activityList = [];
      this.totalCount = 0;
      return;
    }
    this.loading = true;
    this.errorMessage = '';
    const filters = this.buildFilters();
    this.hcaMemberService.getMemberActivityLog(this.memberId, page, this.size, filters).subscribe(res => {
      this.loading = false;
      if (res.error === 0 && res.responseObj) {
        this.activityList = res.responseObj.results || [];
        this.totalCount = res.responseObj.totalRecords || 0;
      } else {
        this.activityList = [];
        this.totalCount = 0;
        this.errorMessage = res && res.message ? res.message : 'Unable to load activity log.';
      }
    }, () => {
      this.loading = false;
      this.activityList = [];
      this.totalCount = 0;
      this.errorMessage = 'Unable to load activity log.';
    });
  }

  buildFilters() {
    const filters: any = {};
    const group = this.filterForm.get('actionGroup').value;
    const from = this.filterForm.get('from').value;
    const to = this.filterForm.get('to').value;
    if (group) {
      filters.actionGroup = group;
    }
    if (from) {
      filters.from = from + 'T00:00:00Z';
    }
    if (to) {
      filters.to = to + 'T23:59:59Z';
    }
    return filters;
  }

  search() {
    this.p = 1;
    this.expandedId = null;
    this.loadActivity(0);
  }

  clearFilters() {
    this.filterForm.reset({actionGroup: '', from: '', to: ''});
    this.search();
  }

  pageChanged(event: number) {
    this.p = event;
    this.expandedId = null;
    this.loadActivity(event - 1);
  }

  toggleDetail(item: any) {
    if (!item || !item.detailJson) {
      return;
    }
    this.expandedId = this.expandedId === item.id ? null : item.id;
  }

  formatDate(value: string): string {
    return this.utilService.dateFormat(value, 'MMM DD, YYYY hh:mm:ss A');
  }

  getGroupLabel(code: string): string {
    return this.groupLabels[code] || code || '—';
  }

  getActionLabel(item: any): string {
    if (!item) {
      return '—';
    }
    const base = this.actionLabels[item.actionCode] || item.actionCode || '—';
    if (item.actionCode === 'MEMBER_UPGRADED' || item.actionCode === 'MEMBER_DOWNGRADED') {
      const detail = this.parseDetail(item.detailJson);
      if (detail && detail.trigger && this.triggerLabels[detail.trigger]) {
        return base + ' (' + this.triggerLabels[detail.trigger] + ')';
      }
    }
    return base;
  }

  getActorLabel(item: any): string {
    if (!item) {
      return '—';
    }
    if (item.actorType === 'SYSTEM') {
      return 'System';
    }
    if (item.actionCode === 'LOGIN_AS') {
      const detail = this.parseDetail(item.detailJson);
      if (detail) {
        const parts = [];
        if (detail.adminEmail) {
          parts.push(detail.adminEmail);
        }
        if (detail.adminUserId) {
          parts.push('id ' + detail.adminUserId);
        }
        if (detail.adminRole) {
          parts.push(detail.adminRole);
        }
        if (parts.length) {
          return parts.join(' · ');
        }
      }
    }
    if (item.actorEmail && item.actorUserId) {
      return item.actorEmail + ' · id ' + item.actorUserId;
    }
    return item.actorEmail || (item.actorUserId ? 'Admin #' + item.actorUserId : 'Admin');
  }

  parseDetail(detailJson: string): any {
    if (!detailJson) {
      return null;
    }
    try {
      const parsed = typeof detailJson === 'string' ? JSON.parse(detailJson) : detailJson;
      return this.sanitizeDetail(parsed);
    } catch (e) {
      return null;
    }
  }

  getDetailEntries(detailJson: string): any[] {
    const parsed = this.parseDetail(detailJson);
    if (!parsed || typeof parsed !== 'object') {
      return [];
    }
    return Object.keys(parsed).map(key => {
      const value = parsed[key];
      return {
        key: key,
        value: typeof value === 'object' ? JSON.stringify(value) : value
      };
    });
  }

  getRawDetail(detailJson: string): string {
    const parsed = this.parseDetail(detailJson);
    if (parsed) {
      try {
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return detailJson;
      }
    }
    return detailJson || '';
  }

  private sanitizeDetail(obj: any): any {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }
    const clean: any = Array.isArray(obj) ? [] : {};
    Object.keys(obj).forEach(key => {
      if (this.sensitiveKeys.indexOf(key) >= 0) {
        return;
      }
      const value = obj[key];
      clean[key] = (value && typeof value === 'object') ? this.sanitizeDetail(value) : value;
    });
    return clean;
  }
}



// WEBPACK FOOTER //
// ./src/app/components/admin/hca-admin/members/members-management/members-activity-log/members-activity-log.component.ts